/**
 * Parlay Workshop Page
 *
 * AI-powered parlay builder featuring Knewton - the sharp analyst companion.
 * Premium feature that helps users build smarter parlays using data insights.
 */

import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParlay } from '../contexts/ParlayContext';
import VibeSelector from '../components/parlay-workshop/VibeSelector';
import ConfigPanel from '../components/parlay-workshop/ConfigPanel';
import KnewtonResults from '../components/parlay-workshop/KnewtonResults';
import PremiumGateModal from '../components/common/PremiumGateModal';
import api from '../services/api';

// Workshop steps
const STEPS = {
  VIBE: 'vibe',
  CONFIG: 'config',
  RESULTS: 'results'
};

export default function ParlayWorkshop() {
  const { userProfile } = useAuth();
  const { parlayItems, removeFromParlay, clearParlay, parlayCount } = useParlay();
  const [currentStep, setCurrentStep] = useState(STEPS.VIBE);
  const [selectedVibe, setSelectedVibe] = useState(null);
  const [config, setConfig] = useState({
    legCount: 3,
    sports: ['nba'],
    betTypes: ['points', 'rebounds', 'assists']
  });
  const [parlayResults, setParlayResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check premium status
  const isPremium = userProfile?.subscription_tier === 'premium' || userProfile?.subscription_tier === 'pro';

  const handleVibeSelect = useCallback((vibe) => {
    setSelectedVibe(vibe);
    setCurrentStep(STEPS.CONFIG);
  }, []);

  const handleConfigSubmit = useCallback(async (configData) => {
    setConfig(configData);
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/parlay-workshop/build', {
        vibe: selectedVibe,
        leg_count: configData.legCount,
        sports: configData.sports,
        prop_types: configData.betTypes
      });

      // Check if backend returned an error (no predictions available)
      if (response.data.error) {
        setError(response.data.error);
        return;
      }

      // Check if we got any legs back
      if (!response.data.legs || response.data.legs.length === 0) {
        setError('No player predictions available for today. Try selecting different sports or check back later.');
        return;
      }

      setParlayResults(response.data);
      setCurrentStep(STEPS.RESULTS);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to build parlay');
    } finally {
      setIsLoading(false);
    }
  }, [selectedVibe]);

  const handleSwapLeg = useCallback(async (legIndex, newLeg) => {
    if (!parlayResults) return;

    const updatedLegs = [...parlayResults.legs];
    updatedLegs[legIndex] = newLeg;
    setParlayResults({
      ...parlayResults,
      legs: updatedLegs
    });
  }, [parlayResults]);

  const handleRemoveLeg = useCallback((legIndex) => {
    if (!parlayResults) return;

    const updatedLegs = parlayResults.legs.filter((_, idx) => idx !== legIndex);
    setParlayResults({
      ...parlayResults,
      legs: updatedLegs
    });
  }, [parlayResults]);

  const handleBuildAnother = useCallback(() => {
    setCurrentStep(STEPS.VIBE);
    setSelectedVibe(null);
    setParlayResults(null);
    setError(null);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === STEPS.CONFIG) {
      setCurrentStep(STEPS.VIBE);
      setSelectedVibe(null);
    } else if (currentStep === STEPS.RESULTS) {
      setCurrentStep(STEPS.CONFIG);
    }
  }, [currentStep]);

  // Premium gate
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-bg-primary p-4 md:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="glass-card p-8 text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-text-primary mb-4">
              Knewton's Parlay Workshop
            </h1>
            <p className="text-text-secondary mb-6">
              Knewton uses AI + our proprietary data to build smarter parlays
              that casual bettors miss.
            </p>
            <ul className="text-left text-text-secondary mb-8 space-y-2 max-w-sm mx-auto">
              <li className="flex items-center gap-2">
                <span className="text-accent-green">✓</span>
                Minutes-adjusted recommendations
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">✓</span>
                Blowout risk detection
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">✓</span>
                Opponent strength analysis
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">✓</span>
                Correlation warnings
              </li>
            </ul>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="btn-primary px-6 py-3 text-lg"
            >
              Upgrade to Premium - $30/mo
            </button>
          </div>
        </div>

        {/* Upgrade Modal */}
        <PremiumGateModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          title="Unlock Parlay Workshop"
          description="Get AI-powered parlay recommendations from Knewton."
          features={[
            "Minutes-adjusted recommendations",
            "Blowout risk detection",
            "Opponent strength analysis",
            "Correlation warnings",
            "Knewton AI parlay builder"
          ]}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🏗️</span>
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">
              Knewton's Parlay Workshop
            </h1>
          </div>
          <p className="text-text-secondary">
            {currentStep === STEPS.VIBE && "Let me help you build something beautiful"}
            {currentStep === STEPS.CONFIG && "Configure your parlay"}
            {currentStep === STEPS.RESULTS && "Here's what I built for you"}
          </p>
        </div>

        {/* Parlay items from Knewton Chat - shown as integrated parlay */}
        {parlayCount > 0 && (
          <div className="glass-card p-4 mb-6 border border-purple-500/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-text-primary flex items-center gap-2">
                <span className="text-purple-400">✨</span>
                Your Parlay
                <span className="text-sm font-normal text-text-secondary">({parlayCount} leg{parlayCount !== 1 ? 's' : ''})</span>
              </h3>
              <button
                onClick={clearParlay}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Clear All
              </button>
            </div>
            <div className="space-y-2 mb-4">
              {parlayItems.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-bg-primary rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-600/20 flex items-center justify-center text-sm font-bold text-purple-400">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{item.player_name}</p>
                      <p className="text-xs text-text-secondary">
                        {item.threshold ? `${item.threshold}+ ` : ''}{item.stat_label || item.stat_type}
                        {item.team_abbr && ` • ${item.team_abbr}`}
                        {item.opponent_abbr && ` vs ${item.opponent_abbr}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-accent-green font-medium">
                      {item.odds ? (item.odds > 0 ? `+${item.odds}` : item.odds) : '-110'}
                    </span>
                    <button
                      onClick={() => removeFromParlay(item)}
                      className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Parlay Summary */}
            {(() => {
              // Calculate combined odds for the parlay
              const decimalOdds = parlayItems.map(item => {
                const odds = item.odds || -110;
                if (odds > 0) return (odds / 100) + 1;
                return (100 / Math.abs(odds)) + 1;
              });
              const combinedDecimal = decimalOdds.reduce((acc, odds) => acc * odds, 1);
              const totalOdds = combinedDecimal >= 2
                ? Math.round((combinedDecimal - 1) * 100)
                : Math.round(-100 / (combinedDecimal - 1));
              const payout = totalOdds > 0
                ? 10 + (10 * (totalOdds / 100))
                : 10 + (10 * (100 / Math.abs(totalOdds)));

              return (
                <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border">
                  <div className="text-center p-2 bg-bg-card rounded-lg">
                    <div className="text-xl font-bold text-text-primary">{parlayCount}</div>
                    <div className="text-[10px] text-text-secondary uppercase">Legs</div>
                  </div>
                  <div className="text-center p-2 bg-bg-card rounded-lg">
                    <div className="text-xl font-bold text-accent-green">
                      {totalOdds > 0 ? `+${totalOdds}` : totalOdds}
                    </div>
                    <div className="text-[10px] text-text-secondary uppercase">Odds</div>
                  </div>
                  <div className="text-center p-2 bg-bg-card rounded-lg">
                    <div className="text-xl font-bold text-text-primary">
                      ${payout.toFixed(2)}
                    </div>
                    <div className="text-[10px] text-text-secondary uppercase">$10 Wins</div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Progress indicator */}
        <div className="flex items-center gap-2 mb-8">
          {Object.values(STEPS).map((step, idx) => (
            <React.Fragment key={step}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep === step
                    ? 'bg-accent-blue text-white'
                    : Object.values(STEPS).indexOf(currentStep) > idx
                    ? 'bg-accent-green text-white'
                    : 'bg-bg-card text-text-secondary'
                }`}
              >
                {Object.values(STEPS).indexOf(currentStep) > idx ? '✓' : idx + 1}
              </div>
              {idx < Object.values(STEPS).length - 1 && (
                <div className={`flex-1 h-1 rounded ${
                  Object.values(STEPS).indexOf(currentStep) > idx
                    ? 'bg-accent-green'
                    : 'bg-bg-card'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Error display */}
        {error && (
          <div className="glass-card bg-red-500/10 border-red-500/30 p-4 mb-6">
            <p className="text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-300 underline mt-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Step content */}
        {currentStep === STEPS.VIBE && (
          <VibeSelector onSelect={handleVibeSelect} />
        )}

        {currentStep === STEPS.CONFIG && (
          <ConfigPanel
            vibe={selectedVibe}
            initialConfig={config}
            onSubmit={handleConfigSubmit}
            onBack={handleBack}
            isLoading={isLoading}
          />
        )}

        {currentStep === STEPS.RESULTS && parlayResults && (
          <KnewtonResults
            results={parlayResults}
            vibe={selectedVibe}
            onSwapLeg={handleSwapLeg}
            onRemoveLeg={handleRemoveLeg}
            onBuildAnother={handleBuildAnother}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
}
